/* eslint-disable unicorn/consistent-function-scoping */
import { createCancelWager } from '@svs/nova-payment-sport';
import { cancelWagers, payWagers } from '@svs/nova-payment-sport/flow';
import type { Wager } from '@svs/nova-payment-sport/wager';

import { getCouponIndex } from '../../../schema/coupon/entry.js';
import { activatedDrawIdSchema } from '../../../schema/marketplace/entry.js';
import { nonNullable } from '../../../schema/utils/entry.js';
import { type IDefinitionService, InternalError, type IWagerClientService, PaymentError } from '../../domain/entry.js';
import {
  accountBalance,
  authenticate,
  cancelConfirm,
  cancelDone,
  isAllowedToPlayMw,
  marketplaceBalance,
  marketplaceShares,
  paymentConfirm,
  paymentDoneMw,
} from '../../middlewares/entry.js';
import { createChunkedExternalWagers, createSrowWager, createWager } from './utils/createWager.js';

/**
 * Wager service to retrieve data from SvS BE.
 *
 * @param definitionService Definition service dependency
 * @returns Wager service to retrieve data
 */
export function wagerClientService(definitionService: IDefinitionService): IWagerClientService {
  /**
   * Performs a payment call.
   *
   * @param coupons Coupons to pay
   * @param root0 function arguments
   * @param root0.wallets Wallets reference
   * @param root0.couponIndices Optional index of the coupons
   * @param root0.showDialog payment confirmation dialog function
   * @param root0.paymentContext Payment context such as user role
   * @returns Payment result object
   */
  const createWagerAsync: IWagerClientService['createWagerAsync'] = async (
    coupons,
    { wallets, couponIndices, showDialog, paymentContext }
    // eslint-disable-next-line complexity
  ) => {
    const wagers: Wager[] = [];
    const singleRowsCoupons = coupons.filter((coupon) => coupon.model.couponType === 'singlerows');
    const externalCoupon = coupons.find((coupon) => coupon.model.couponType === 'external_system');
    if (singleRowsCoupons.length === coupons.length) {
      const [couponIndex] = couponIndices;

      if (!getCouponIndex(couponIndex)) {
        throw new InternalError('Coupon index is required to create an singlerows wager');
      }

      const wallet = wallets?.[couponIndex] ?? 'WALLET_PLAYER';
      wagers.push(createSrowWager(coupons, { wallet, paymentContext }));
    } else if (externalCoupon) {
      const couponIndex = couponIndices[coupons.indexOf(externalCoupon)];

      if (!getCouponIndex(couponIndex)) {
        throw new InternalError('Coupon index is required to create an external wager');
      }

      const wallet = wallets?.[couponIndex] ?? 'WALLET_PLAYER';
      const definition = await definitionService.resolveDefinitionAsync(externalCoupon.product);

      if (!definition) {
        throw new InternalError(`Unable to load definition for product: ${externalCoupon.product}`);
      }

      wagers.push(
        ...createChunkedExternalWagers(externalCoupon, {
          wallet,
          paymentContext,
          definition,
        })
      );
    } else {
      for (const coupon of coupons) {
        const couponIndex = couponIndices[coupons.indexOf(coupon)];
        if (typeof couponIndex !== 'number') {
          throw new InternalError('Coupon index is required to create a wager');
        }
        const wallet = wallets?.[couponIndex] ?? 'WALLET_PLAYER';
        wagers.push(createWager(coupon, { wallet, couponIndex, paymentContext }));
      }
    }

    const preMiddlewares = [
      authenticate(paymentContext),
      ...(paymentContext.role === 'Retailer' ? [] : [isAllowedToPlayMw(showDialog, 'tipsspel')]),
      accountBalance(showDialog, paymentContext.paymentUrls, paymentContext.trackEventFunction),
      marketplaceShares(showDialog, paymentContext.marketplaceWallets),
      marketplaceBalance(showDialog),
      paymentConfirm(showDialog),
    ];
    const postMiddlewares = [paymentDoneMw(showDialog, paymentContext.paymentUrls, paymentContext.trackEventFunction)];

    const [response, error, clientErrorCode] = await payWagers({
      wagers,
      config: { haltPaymentOnException: false, trackTransaction: paymentContext.trackTransaction },
      preMiddlewares,
      postMiddlewares,
    });

    const paidItems = response.getPaidPayableItems();
    const unpaidItems = response.getUnpaidPayableItems();
    const paidItemsCouponIndices = paidItems
      .map((item) => (getCouponIndex(item.couponIndex) ? item.couponIndex : undefined))
      .filter((value) => value !== undefined);

    if (paidItems.length === 0) {
      if (error) {
        throw error;
      }

      if (clientErrorCode) {
        throw new PaymentError('Failed to place wager', undefined, clientErrorCode);
      }
      throw new InternalError('Failed to place wager');
    }

    const paidActivatedDrawIds = [
      ...new Set(
        paidItems
          .map((item) => activatedDrawIdSchema.safeParse(item.activatedDrawId).data)
          .filter((activatedDrawId) => nonNullable(activatedDrawId))
      ),
    ];

    const result = {
      transactionIds: response.getPaidPayableItemsDTO().map((d) => d.transactionId),
      paidAmount: response.getPaidAmount(),
      paidItemsCount: paidItems.length,
      unpaidItemsCount: unpaidItems.length,
      paidItemsCouponIndices,
      paidActivatedDrawIds,
    } as const;

    return result;
  };

  /**
   * Cancels a wager.
   *
   * @param root0 props
   * @param root0.wagerId Id for wager to remove
   * @param root0.price totalPrice of wager
   * @param root0.product product id of wager
   * @param root0.isMarketplace whether the wager is a marketplace wager or not
   * @param root0.showDialog Dialog function to show dialog
   * @param root0.callbackOnDone Callback function to call after successful removal
   * @param root0.trackTransaction optional ecommerce tracking callback
   * @param root0.icon Icon to display in the dialog
   */
  const removeWagerAsync: IWagerClientService['removeWagerAsync'] = async ({
    wagerId,
    price,
    product,
    isMarketplace,
    showDialog,
    callbackOnDone,
    trackTransaction,
    icon,
  }) => {
    const preMiddlewares = [cancelConfirm(showDialog, icon)];
    const postMiddlewares = [cancelDone(showDialog, callbackOnDone)];
    const wager = createCancelWager();
    wager.setPrice(price);
    wager.setTransactionId(wagerId);
    wager.setProduct(product);
    if (isMarketplace !== undefined) {
      wager.setIsMarketplace(isMarketplace);
    }
    const [, error] = await cancelWagers({
      wagers: [wager],
      preMiddlewares,
      postMiddlewares,
      trackTransaction,
    });
    if (error) {
      throw new Error(`Failed to cancel wager with id ${wagerId}: ${error.message}`);
    }
  };

  return {
    createWagerAsync,
    removeWagerAsync,
  };
}
